import { useState, useEffect } from 'react'
import { useParams, Link } from 'wouter'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Calendar, FileText, Download, TrendingUp, Target, Users, ChevronRight, AlertCircle } from 'lucide-react'
import { useToast } from "@/hooks/use-toast"
import { format } from 'date-fns'

interface MaturityResult {
  id: string
  completed_at: string
  quadrant_placement: string
  overall_score: number
  pdf_exported: boolean
  export_filename?: string
}

interface ROIResult {
  id: string
  completed_at: string
  source: string
  total_benefit: number
  payback_period: number
  roi_percentage: number
  selected_pillars: string[]
  pdf_exported: boolean
  ppt_exported: boolean
  export_filenames?: string[]
}

interface AdvancedROIResult {
  id: string
  completed_at: string
  npv: number
  irr: number
  payback_period: number
  sensitivity_model: string
  exported: boolean
  export_filenames?: string[]
}

interface ValueAssessmentResult {
  id: string
  created_at: string
  finalized_at?: string
  strategic_objectives: string[]
  challenges_identified: string[]
  automation_priorities: string[]
  exported: boolean
  export_filenames?: string[]
}

interface ProjectHistoryData {
  project: {
    id: string
    name: string
    description: string
    status: string
    created_at: string
  }
  maturity_results: MaturityResult[]
  roi_results: ROIResult[]
  advanced_roi_results: AdvancedROIResult[]
  value_assessment_results: ValueAssessmentResult[]
}

export default function ProjectHistoryScreen() {
  const { projectId } = useParams()
  const { toast } = useToast()
  const [historyData, setHistoryData] = useState<ProjectHistoryData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchProjectHistory = async () => {
      try {
        const response = await fetch(`/api/projects/${projectId}/history`)
        if (!response.ok) {
          throw new Error('Failed to fetch project history')
        }
        const data = await response.json()
        setHistoryData(data)
      } catch (err) {
        console.error('Error fetching project history:', err)
        setError('Failed to load project history')
        toast({
          title: "Error",
          description: "Unable to load project history data",
          variant: "destructive",
        })
      } finally {
        setLoading(false)
      }
    }

    if (projectId) {
      fetchProjectHistory()
    }
  }, [projectId, toast])

  const handleExportDownload = async (resultId: string, moduleType: string, exportType: string) => {
    try {
      const response = await fetch(`/api/projects/${projectId}/export/${moduleType}/${resultId}/${exportType}`)
      if (!response.ok) {
        throw new Error('Export file not found')
      }
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${moduleType}-${resultId}-export.${exportType}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      
      toast({
        title: "Download Started",
        description: "Your export file is downloading",
      })
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Unable to download the export file",
        variant: "destructive",
      })
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading project history...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !historyData) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center min-h-96">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Unable to Load Project History</h3>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button asChild>
                  <Link href="/dashboard">Return to Dashboard</Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const { project, maturity_results, roi_results, advanced_roi_results, value_engineering_results } = historyData

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Project History</h1>
          <p className="text-muted-foreground">Complete assessment timeline for {project.name}</p>
        </div>
        <Button asChild variant="outline">
          <Link href="/dashboard">
            <ChevronRight className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Link>
        </Button>
      </div>

      {/* Project Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            {project.name}
          </CardTitle>
          <CardDescription>{project.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium">Status</p>
              <Badge variant={project.status === 'Active' ? 'default' : 'secondary'}>
                {project.status}
              </Badge>
            </div>
            <div>
              <p className="text-sm font-medium">Created</p>
              <p className="text-sm text-muted-foreground">
                {format(new Date(project.created_at), 'MMM d, yyyy')}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium">Project ID</p>
              <p className="text-sm text-muted-foreground font-mono">{project.id}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* R2R Maturity Assessment Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            R2R Maturity Assessment Results
          </CardTitle>
          <CardDescription>
            {maturity_results.length} assessment{maturity_results.length !== 1 ? 's' : ''} completed
          </CardDescription>
        </CardHeader>
        <CardContent>
          {maturity_results.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No maturity assessments completed yet</p>
              <Button asChild className="mt-4">
                <Link href="/maturity">Start Maturity Assessment</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {maturity_results.map((result, index) => (
                <div key={result.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Badge variant={index === 0 ? 'default' : 'secondary'}>
                        {index === 0 ? 'Most Recent' : `Assessment ${maturity_results.length - index}`}
                      </Badge>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(result.completed_at), 'MMM d, yyyy - h:mm a')}
                      </div>
                    </div>
                    {result.pdf_exported && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleExportDownload(result.id, 'maturity', 'pdf')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Export PDF
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm font-medium">Quadrant Placement</p>
                      <p className="text-sm">{result.quadrant_placement}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Overall Score</p>
                      <p className="text-sm">{result.overall_score}/100</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Export Status</p>
                      <Badge variant={result.pdf_exported ? 'default' : 'secondary'}>
                        {result.pdf_exported ? 'Exported' : 'Not Exported'}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Standard ROI Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Standard ROI Results
          </CardTitle>
          <CardDescription>
            {roi_results.filter(r => r.source === 'Standard ROI').length} standard ROI analysis completed
          </CardDescription>
        </CardHeader>
        <CardContent>
          {roi_results.filter(r => r.source === 'Standard ROI').length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No standard ROI analyses completed yet</p>
              <Button asChild className="mt-4">
                <Link href="/roi/standard">Start Standard ROI</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {roi_results
                .filter(r => r.source === 'Standard ROI')
                .map((result, index) => (
                  <div key={result.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Badge variant={index === 0 ? 'default' : 'secondary'}>
                          {index === 0 ? 'Most Recent' : `Analysis ${roi_results.filter(r => r.source === 'Standard ROI').length - index}`}
                        </Badge>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          {format(new Date(result.completed_at), 'MMM d, yyyy - h:mm a')}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {result.pdf_exported && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExportDownload(result.id, 'roi', 'pdf')}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            PDF
                          </Button>
                        )}
                        {result.ppt_exported && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExportDownload(result.id, 'roi', 'ppt')}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            PPT
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm font-medium">Total Benefit</p>
                        <p className="text-sm">${result.total_benefit.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Payback Period</p>
                        <p className="text-sm">{result.payback_period} months</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">5-Year ROI</p>
                        <p className="text-sm">{result.roi_percentage}%</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Pillars Selected</p>
                        <p className="text-sm">{result.selected_pillars.join(', ')}</p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Advanced ROI Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Advanced ROI Results
          </CardTitle>
          <CardDescription>
            {advanced_roi_results.length} advanced ROI analysis completed
          </CardDescription>
        </CardHeader>
        <CardContent>
          {advanced_roi_results.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No advanced ROI analyses completed yet</p>
              <Button asChild className="mt-4">
                <Link href="/roi/advanced">Start Advanced ROI</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {advanced_roi_results.map((result, index) => (
                <div key={result.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Badge variant={index === 0 ? 'default' : 'secondary'}>
                        {index === 0 ? 'Most Recent' : `Analysis ${advanced_roi_results.length - index}`}
                      </Badge>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(result.completed_at), 'MMM d, yyyy - h:mm a')}
                      </div>
                    </div>
                    {result.exported && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleExportDownload(result.id, 'advanced-roi', 'pdf')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Export
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm font-medium">NPV</p>
                      <p className="text-sm">${result.npv.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">IRR</p>
                      <p className="text-sm">{result.irr}%</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Payback Period</p>
                      <p className="text-sm">{result.payback_period} months</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Sensitivity Model</p>
                      <p className="text-sm">{result.sensitivity_model}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Value Assessment Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Value Assessment Results
          </CardTitle>
          <CardDescription>
            {value_engineering_results.length} value assessment session{value_engineering_results.length !== 1 ? 's' : ''} completed
          </CardDescription>
        </CardHeader>
        <CardContent>
          {value_engineering_results.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No value assessments completed yet</p>
              <Button asChild className="mt-4">
                <Link href={`/value-assessment/${projectId}`}>Start Value Assessment</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {value_engineering_results.map((result, index) => (
                <div key={result.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Badge variant={index === 0 ? 'default' : 'secondary'}>
                        {index === 0 ? 'Most Recent' : `Session ${value_engineering_results.length - index}`}
                      </Badge>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        Started: {format(new Date(result.created_at), 'MMM d, yyyy')}
                        {result.finalized_at && (
                          <span> • Finalized: {format(new Date(result.finalized_at), 'MMM d, yyyy')}</span>
                        )}
                      </div>
                    </div>
                    {result.exported && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleExportDownload(result.id, 'value-assessment', 'pdf')}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          PDF
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleExportDownload(result.id, 'value-assessment', 'ppt')}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          PPT
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium">Strategic Objectives</p>
                      <p className="text-sm text-muted-foreground">
                        {result.strategic_objectives.slice(0, 3).join(', ')}
                        {result.strategic_objectives.length > 3 && ` (+${result.strategic_objectives.length - 3} more)`}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Key Challenges</p>
                      <p className="text-sm text-muted-foreground">
                        {result.challenges_identified.slice(0, 2).join(', ')}
                        {result.challenges_identified.length > 2 && ` (+${result.challenges_identified.length - 2} more)`}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Automation Priorities</p>
                      <p className="text-sm text-muted-foreground">
                        {result.automation_priorities.slice(0, 3).join(', ')}
                        {result.automation_priorities.length > 3 && ` (+${result.automation_priorities.length - 3} more)`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}